import { chain } from 'lodash';
import { MetricMode, Organisation, OrganisationType, User } from './types';

export function getUserProjectIdsThroughOrganisation(user: User | undefined | null): number[] {
  if (!user) {
    return [];
  }
  const orgProjectIds = user.organisations.flatMap(org => org.projectAccesses);
  const sortedIds = chain([...orgProjectIds])
    .sortBy(id => -id)
    .uniq()
    .value();

  return sortedIds;
}

export function getUserProjectIdsWithDirectAccess(user: User | undefined | null): number[] {
  const directProjectIds = user ? user.directProjectAccesses : [];
  const sortedIds = chain([...directProjectIds])
    .sortBy(id => -id)
    .uniq()
    .value();

  return sortedIds;
}

export function getUserProjectIds(user: User | undefined | null): number[] {
  const directProjectIds = getUserProjectIdsWithDirectAccess(user);
  const orgProjectIds = getUserProjectIdsThroughOrganisation(user);

  return chain([...directProjectIds, ...orgProjectIds])
    .sortBy(id => -id)
    .uniq()
    .value();
}

export function getUserSamplingPointIds(user: User | undefined | null): number[] {
  if (!user) {
    return [];
  }
  const directIds = user.directSamplingPointAccesses;
  const organisationIds = user.organisations.flatMap(org => org.samplingPointAccesses);
  return chain([...directIds, ...organisationIds])
    .sortBy(id => -id)
    .uniq()
    .value();
}

export function getUserSampleIds(user: User | undefined | null): number[] {
  if (!user) {
    return [];
  }
  const directIds = user.directSampleAccesses;
  const organisationIds = user.organisations.flatMap(org => org.sampleAccesses);
  return chain([...directIds, ...organisationIds])
    .sortBy(id => -id)
    .uniq()
    .value();
}

export function getUserDefaultMetric(user: User | undefined | null): MetricMode | null {
  const orgDefaults = chain(user?.organisations || [])
    .map(o => o.defaultMetric)
    .compact()
    .uniq()
    .value();
  return Object.values(MetricMode).find(mode => orgDefaults.includes(mode)) || MetricMode.ARGI;
}

export function getProjectOrganisationIds(
  organisation: Pick<Organisation, 'projectAccesses'> | undefined | null,
): number[] {
  if (!organisation) {
    return [];
  }
  return organisation.projectAccesses;
}

export function canViewInternalLabSheetUrls(user: User | undefined | null): boolean {
  if (!user) {
    return false;
  }
  return user.organisations.some(org => org.type === OrganisationType.RESISTOMAP);
}

export function redactLabSheetUrlForUser<T extends { labSheetUrl: string | null }>(
  value: T,
  user: User | undefined | null,
): T {
  if (canViewInternalLabSheetUrls(user)) {
    return value;
  }
  return { ...value, labSheetUrl: null };
}
